<div class="chat" *transloco="let t">
  @if (!composerOnly()) {
    <div class="chat__surface">
      <div
        #scrollContainer
        class="chat__log"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        [attr.aria-label]="t(ts.logAria)"
        (scroll)="onScroll()"
        (wheel)="onUserScrollIntent()"
        (touchmove)="onUserScrollIntent()"
        (keydown)="onUserScrollIntent()"
      >
        @if (loading() && isEmpty()) {
          <div class="chat__state">
            <ds-spinner></ds-spinner>
          </div>
        } @else if (isEmpty() && draftAttachments().length === 0) {
          <!-- Hidden while composing an attachment: the «Файл прикреплён» frame keeps the dialogue blank. -->
          <div class="chat__state chat__state--empty">
            <ds-icon-folder-remove
              size="m"
              class="chat__empty-icon"
              aria-hidden="true"
            ></ds-icon-folder-remove>
            <p class="chat__empty-title">{{ emptyTitleText() ?? t(ts.emptyTitle) }}</p>
            <p class="chat__empty-hint">{{ slaText() ?? t(ts.emptyHint) }}</p>
            <div class="empty-state-actions">
              <ng-content select="[emptyStateActions]"></ng-content>
            </div>
          </div>
        } @else {
          @for (row of rows(); track trackRow($index, row)) {
            @if (row.kind === 'day') {
              <div class="day-divider" data-test="report chat day divider">
                <time class="day-label" [attr.datetime]="row.dayDate">
                  {{ row.dayLabelKey ? t(row.dayLabelKey) : row.dayLabel }}
                </time>
              </div>
            } @else if (row.kind === 'divider') {
              <div class="chat__divider" data-test="report chat unread divider">
                <span class="chat__divider-line"></span>
                <span class="chat__divider-label">{{ t(ts.newMessages) }}</span>
                <span class="chat__divider-line"></span>
              </div>
            } @else if (row.message) {
              @let m = row.message;
              @if (m.systemText) {
                <div class="chat__system" data-test="report chat system message">
                  {{ m.systemText }}
                </div>
                @if (m.actions?.length) {
                  <div
                    class="chat__msg-actions chat__msg-actions--center"
                    data-test="report chat actions"
                  >
                    @for (action of m.actions; track action.id) {
                      <button
                        type="button"
                        class="chat__action"
                        [class.chat__action--primary]="action.variant === 'primary'"
                        [disabled]="action.disabled"
                        [attr.data-test]="`report chat action ${action.id}`"
                        (click)="actionClicked.emit({ message: m, actionId: action.id })"
                      >
                        {{ action.label }}
                      </button>
                    }
                  </div>
                }
              } @else if (m.authorRole === 'system') {
                <!-- System notice with no localized text: the host did not resolve
                     its systemKey (e.g. an unknown key). Render nothing rather than
                     an empty bubble or the raw payload. -->
              } @else {
                <div
                  class="chat__msg"
                  [class.chat__msg--mine]="ownSide(m)"
                  [class.chat__msg--theirs]="!ownSide(m)"
                  [class.chat__msg--pending]="m.pending"
                  [class.chat__msg--failed]="m.failed"
                  [attr.data-msg-id]="m.id"
                  [attr.data-test-side]="ownSide(m) ? 'own' : 'other'"
                  data-test="report chat message"
                >
                  <ng-template #messageMeta>
                    @if (showTimestamps() && row.timeFormat) {
                      <time
                        class="message-time"
                        data-testid="report-chat-message-time"
                        [attr.datetime]="m.createdAt"
                        [attr.title]="m.createdAt | date: 'dd.MM.yyyy, HH:mm'"
                        [attr.aria-label]="m.createdAt | date: 'dd.MM.yyyy, HH:mm'"
                      >
                        {{ m.createdAt | date: row.timeFormat }}
                      </time>
                    }

                    @if (m.mine && (m.pending || m.failed || m.seenByStaff)) {
                      @if (m.pending) {
                        <span class="chat__receipt">{{ t(ts.sending) }}</span>
                      } @else if (m.failed) {
                        <span class="chat__receipt chat__receipt--failed">
                          {{ t(ts.sendFailed) }}
                        </span>
                      } @else {
                        <ds-icon-double-check
                          size="s"
                          class="chat__receipt-check"
                          [attr.aria-label]="t(ts.seen)"
                        ></ds-icon-double-check>
                      }
                    }
                  </ng-template>
                  @if (m.body) {
                    <div
                      class="chat__bubble"
                      [class.-with-time]="showTimestamps() && !!row.timeFormat"
                      [attr.aria-label]="t(authorLabel(m))"
                    >
                      <!-- Text/link runs, never HTML: the body is author-supplied,
                           so it must not reach `innerHTML`. Each run is bound via
                           `[textContent]` rather than interpolated, because
                           `.chat__body` is `white-space: pre-wrap` — a text node
                           written inline would carry the template's own indentation
                           into the rendered message. -->
                      <p class="chat__body">
                        <span class="message-text">
                          @for (part of row.bodySegments ?? []; track $index) {
                            @if (part.href) {
                              <a
                                class="chat__link"
                                [href]="part.href"
                                [textContent]="part.text"
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                data-test="report chat link"
                              ></a>
                            } @else {
                              <span [textContent]="part.text"></span>
                            }
                          }
                        </span>
                        @if (showTimestamps() && row.timeFormat) {
                          <span class="message-meta">
                            <ng-container [ngTemplateOutlet]="messageMeta" />
                          </span>
                        }
                      </p>
                      @if (
                        !(showTimestamps() && row.timeFormat) &&
                        m.mine &&
                        (m.pending || m.failed || m.seenByStaff)
                      ) {
                        <span class="chat__meta">
                          <ng-container [ngTemplateOutlet]="messageMeta" />
                        </span>
                      }
                    </div>
                  }
                  @for (file of attachmentsOf(m); track $index) {
                    @if (file.previewUrl) {
                      <!-- Image: preview in the lightbox (the storage URL forces a
                           download, so a link would never just show the picture). -->
                      <button
                        type="button"
                        class="chat__file chat__file--image chat__file--link"
                        [attr.title]="t(ts.fileOpenAria)"
                        (click)="openMessageImage(file)"
                        data-test="report chat file"
                      >
                        <img class="chat__file-thumb" [src]="file.previewUrl" [alt]="file.label" />
                        <span class="chat__file-label">{{ file.label }}</span>
                      </button>
                    } @else {
                      <!-- Non-image: open / download in a new tab. -->
                      <a
                        class="chat__file"
                        [class.chat__file--link]="!!file.url"
                        [href]="file.url || null"
                        [attr.target]="file.url ? '_blank' : null"
                        [attr.rel]="file.url ? 'noopener noreferrer' : null"
                        [attr.title]="file.url ? t(ts.fileOpenAria) : null"
                        data-test="report chat file"
                      >
                        <ds-icon-attach size="s" aria-hidden="true"></ds-icon-attach>
                        <span class="chat__file-label">{{ file.label }}</span>
                      </a>
                    }
                  }
                  @if (showTimestamps() && row.timeFormat && !m.body) {
                    <span class="file-meta"><ng-container [ngTemplateOutlet]="messageMeta" /></span>
                  }
                  @if (m.actions?.length) {
                    <div class="chat__msg-actions" data-test="report chat actions">
                      @for (action of m.actions; track action.id) {
                        <button
                          type="button"
                          class="chat__action"
                          [class.chat__action--primary]="action.variant === 'primary'"
                          [disabled]="action.disabled"
                          [attr.data-test]="`report chat action ${action.id}`"
                          (click)="actionClicked.emit({ message: m, actionId: action.id })"
                        >
                          {{ action.label }}
                        </button>
                      }
                    </div>
                  }
                  @if (m.mine && m.failed) {
                    <button
                      type="button"
                      class="chat__retry"
                      data-test="report chat retry"
                      (click)="onRetry(m)"
                    >
                      {{ t(ts.retry) }}
                    </button>
                  }
                </div>
              }
            }
          }
        }
      </div>

      @if (hasUnreadBelow()) {
        <button
          type="button"
          class="chat__jump"
          data-test="report chat jump"
          [attr.aria-label]="t(ts.newMessages)"
          (click)="jumpToBottom()"
        >
          <ds-icon-arrow-down size="m"></ds-icon-arrow-down>
        </button>
      }
    </div>
  }

  <div class="chat__footer">
    <!-- Host slot directly above the composer (e.g. the modal's category chips
         on a new appeal) — keeps them next to the input, not at the top. -->
    <ng-content select="[composerTop]"></ng-content>
    @if (draftChips().length > 0) {
      <div class="chat__drafts">
        <!-- Tracked by position, like the removal below: labels repeat here
             (two clipboard screenshots are both `image.png`), so a label is not
             a key for this list — its identity is its index into the draft. -->
        @for (chip of draftChips(); track $index) {
          <lab-attachment-chip
            [label]="chip.source.label"
            [previewUrl]="chip.safePreviewUrl"
            [previewAria]="chip.safePreviewUrl ? t(ts.previewAria) : null"
            [removeAria]="t(ts.removeAttachmentAria)"
            dataTest="report chat draft attachment"
            previewDataTest="report chat draft preview"
            removeDataTest="report chat draft remove"
            (previewRequested)="openAttachmentPreview(chip)"
            (removeRequested)="removeAttachment.emit($index)"
          ></lab-attachment-chip>
        }
      </div>
    }

    <div
      class="chat__composer"
      [class.chat__composer--pristine]="
        !composerOnly() && isEmpty() && draftAttachments().length === 0 && !textControl.value
      "
      [class.chat__composer--disabled]="disabled()"
      [class.chat__composer--error]="sendError()"
      [class.chat__composer--hover]="composerStateOverride() === 'hover'"
      [class.chat__composer--focus]="composerStateOverride() === 'focus'"
      [attr.title]="disabledReason()"
    >
      <textarea
        #textarea
        class="chat__textarea"
        cdkTextareaAutosize
        cdkAutosizeMinRows="1"
        cdkAutosizeMaxRows="7"
        [class.chat__textarea--scrollable]="composerScrollable()"
        [formControl]="textControl"
        [attr.maxlength]="maxLength"
        [attr.aria-label]="
          (disabled() ? disabledPlaceholderText() : null) ?? placeholderText() ?? t(ts.placeholder)
        "
        [placeholder]="
          disabled()
            ? (disabledPlaceholderText() ?? t(ts.placeholderDisabled))
            : sendError() && !textControl.value
              ? t(ts.sendFailed)
              : (placeholderText() ?? t(ts.placeholder))
        "
        (keydown.enter)="onTextareaKeydown($event)"
        (input)="onComposerInput(textarea)"
        data-test="report chat input"
      ></textarea>

      <!-- With an empty composer the red message replaces the placeholder instead. -->
      @if (sendError() && textControl.value) {
        <p class="chat__error" role="alert" aria-live="assertive">{{ t(ts.sendFailed) }}</p>
      }

      <div class="chat__actions">
        <span class="chat__actions-left">
          @if (allowAttachments()) {
            <button
              type="button"
              class="chat__iconbtn"
              data-test="report chat attach"
              [disabled]="disabled()"
              [attr.aria-label]="t(ts.attachAria)"
              (click)="attachRequested.emit()"
            >
              <ds-icon-attach size="m"></ds-icon-attach>
            </button>
          }
          @if (allowScreenshot()) {
            <button
              type="button"
              class="chat__iconbtn"
              data-test="report chat screenshot"
              [disabled]="disabled()"
              [attr.aria-label]="t(ts.screenshotAria)"
              (click)="screenshotRequested.emit()"
            >
              <ds-icon-photo size="m"></ds-icon-photo>
            </button>
          }
        </span>

        <span class="chat__actions-right">
          @if (textControl.value.length > maxLength - 200) {
            <span
              class="chat__counter"
              [class.chat__counter--over]="textControl.value.length > maxLength"
            >
              {{ textControl.value.length }}/{{ maxLength }}
            </span>
          }
          <button
            type="button"
            class="chat__iconbtn chat__send"
            [class.chat__send--armed]="
              !sendDisabled() &&
              (!!textControl.value || sendError() || draftAttachments().length > 0)
            "
            data-test="report chat send"
            [disabled]="
              disabled() || sendDisabled() || sending() || textControl.value.length > maxLength
            "
            [attr.aria-label]="t(ts.sendAria)"
            (click)="submit()"
          >
            @if (sending()) {
              <ds-spinner size="s"></ds-spinner>
            } @else {
              <ds-icon-airplane size="m"></ds-icon-airplane>
            }
          </button>
        </span>
      </div>
    </div>
  </div>

  <!-- Fullscreen preview of a draft image attachment (screenshot check before send). -->
  @if (previewedAttachment(); as preview) {
    <div
      #lightbox
      class="chat__lightbox"
      data-test="report chat attachment lightbox"
      (click)="closeAttachmentPreview()"
      (keydown.escape)="closeAttachmentPreview()"
      tabindex="-1"
    >
      <button
        type="button"
        class="chat__lightbox-close"
        [attr.aria-label]="t(ts.previewCloseAria)"
        (click)="closeAttachmentPreview()"
      >
        <ds-icon-close size="m"></ds-icon-close>
      </button>
      <img
        class="chat__lightbox-img"
        [src]="preview.safePreviewUrl"
        [alt]="preview.label"
        (click)="$event.stopPropagation()"
      />
    </div>
  }
</div>
